import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useCompanyApi } from "../../hooks/useCompanyApi";
import GeneralSetupTab from "../../components/GeneralSetupTab";
import CenterWrapper from "../../components/CenterWrapper";
import { CountryDropdown } from "react-country-region-selector";
import TimezoneInfo from "./TimezoneInfo";
import SectionHeader from "../../components/SectionHeader";
import Input from "../../components/Input";
import PrimaryButton from "../../components/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton";
import api from "../../api";

const CompanyInfo = () => {
  const { company, fetchCompany, updateCompany, setLoading } = useCompanyApi();
  const [feedback, setFeedback] = useState({});
  const [chatbot, setChatbot] = useState(null);
  const chatbotUuid = localStorage.getItem("chatbot_uuid");

  const { register, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      name: "",
      url: "",
      address: {
        street: "",
        street_2: "",
        city: "",
        state: "",
        postal_code: "",
        country: "",
      },
    },
  });

  useEffect(() => {
    fetchCompany();
  }, []);

  useEffect(() => {
    if (company) {
      reset({
        name: company.name || "",
        url: company.url || "",
        address: {
          street: company.address?.street || "",
          street_2: company.address?.street_2 || "",
          city: company.address?.city || "",
          state: company.address?.state || "",
          postal_code: company.address?.postal_code || "",
          country: company.address?.country || "",
        },
      });
    }
  }, [company, reset]);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/chatbot/${chatbotUuid}`)
      .then((response) => {
        const data = response.data;
        setChatbot({ ...data, ai_model: data.ai_model ?? "" });
      })
      .finally(() => setLoading(false));
  }, []);

  const onSubmit = async data => {
    try {
      const updatedCompany = {
        name: data.name,
        url: data.url,
        address: {
          street: data.address.street,
          street_2: data.address.street_2,
          city: data.address.city,
          state: data.address.state,
          postal_code: data.address.postal_code,
          country: data.address.country,
        },
      };

      const error = await updateCompany(updatedCompany);

      if (!error) {
        setFeedback({
          status: "success",
          msg: "Company updated successfully!",
        });
      } else {
        throw error;
      }
    } catch (error) {
      setFeedback({
        status: "error",
        msg: "There was a problem updating the company!",
      });
    }

    setTimeout(() => setFeedback({}), 10000);
  };

  const onOAIVersionSubmit = async () => {
    setLoading(true);
    try {
      const ai_model = !chatbot.ai_model ? null : chatbot.ai_model;
      await api.put(`/api/chatbot/${chatbotUuid}`, { ai_model });
      setFeedback({ status: "success", msg: "AI model updated successfully" });
      return null;
    } catch (error) {
      setFeedback({ status: "error", msg: "Failed to update AI model" });
      return null;
    } finally {
      setLoading(false);
    }

    setTimeout(() => setFeedback({}), 8000);
  };


  if (!company) return null;

  return (
    <CenterWrapper>
      <div style={{ paddingBottom: "10px" }}>
        <div>
          <GeneralSetupTab
            title="Account Settings"
            subtitle="Update your company settings, team, and billing."
            pageType="settings"
            activeTab="company_info"
          />

          <div>
            {feedback.status && (
              <div className="sm:col-span-full">
                <div
                  className={`flex items-center p-4 rounded-lg ${
                    feedback.status === "success"
                      ? "text-green-800 bg-green-50"
                      : "text-red-800 bg-red-50"
                  }`}
                  role="alert"
                >
                  <svg
                    className="flex-shrink-0 w-4 h-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    {feedback.status === "success" ? (
                      <path d="M16.707 5.293a1 1 0 0 0-1.414 0L9 11.586 6.707 9.293a1 1 0 0 0-1.414 1.414l3 3a1 1 0 0 0 1.414 0l7-7a1 1 0 0 0 0-1.414z" />
                    ) : (
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1-5a1 1 0 0 1-2 0V7a1 1 0 0 1 2 0v6zm-1-9a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z"
                        clipRule="evenodd"
                      />
                    )}
                  </svg>
                  <div className="ml-3 text-sm font-medium">{feedback.msg}</div>
                  <button
                    onClick={() => setFeedback({})}
                    className="ml-auto -mx-1.5 -my-1.5 rounded-lg focus:ring-2 p-1.5 inline-flex items-center justify-center h-8 w-8"
                    aria-label="Close"
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="w-3 h-3"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 14 14"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M1 1l12 12m0-12L1 13"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="pb-6 border-b mb-6"
            >
              <div className="flex items-start gap-6 justify-between max-sm:flex-col">
                <div className="sm:max-w-sm">
                  <SectionHeader
                    title="Company Information"
                    description="Enter your organization name and website URL that you want
                    to appear on your invoices."
                  />
                </div>

                <div className="bg-white p-6 rounded-lg shadow border border-gray-200 w-full sm:max-w-xl">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Organization Name
                    </label>
                    <Input placeholder="Name" value={company?.name} disabled />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website URL
                    </label>
                    <div className="relative mt-1">
                      <Input
                        placeholder="https://example.com"
                        value={company?.url}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>

              <hr className="my-6 border-t border-gray-300" />

              <div className="flex items-start gap-6 justify-between max-sm:flex-col">
                <div className="sm:max-w-sm">
                  <SectionHeader
                    title="Billing Address"
                    description="Your billing address, this will appear on your invoices."
                  />
                </div>

                <div className="bg-white p-6 rounded-lg shadow border border-gray-200 w-full sm:max-w-xl">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address
                    </label>
                    <Input name="address.street" register={register} />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Apartment, suite, etc. (optional)
                    </label>
                    <Input name="address.street_2" register={register} />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 whitespace-nowrap mb-2">
                        City
                      </label>
                      <Input name="address.city" register={register} />
                    </div>

                    <div className="col-span-1 max-[1200px]:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 whitespace-nowrap mb-2">
                        State / Province
                      </label>
                      <Input name="address.state" register={register} />
                    </div>

                    <div className="col-span-1 max-[1200px]:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 whitespace-nowrap mb-2">
                        ZIP Code / Postal Code
                      </label>
                      <Input name="address.postal_code" register={register} />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <div className="mt-2">
                      <CountryDropdown
                        value={watch("address.country")}
                        onChange={val => setValue("address.country", val)}
                        className="border-none focus:outline-inputFocused outline-secondaryBorder disabled:bg-secondaryHover placeholder-inputPlaceholder text-inputText outline outline-1 focus:outline-2 w-full px-3 py-2 rounded-lg shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] text-base font-normal disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <hr className="my-6 border-t border-gray-300" />

                  <div className="flex gap-4 justify-end items-start">
                    <div>
                      <SecondaryButton
                        onClick={() => {
                          reset();
                        }}
                        type="button"
                      >
                        Cancel
                      </SecondaryButton>
                    </div>

                    <div>
                      <PrimaryButton>Save Changes</PrimaryButton>
                    </div>
                  </div>
                </div>
              </div>
            </form>

            <TimezoneInfo
              defaultValues={{
                timezone: company.timezone,
              }}
              companyTimeslots={company?.available_timeslots}
              setLoading={setLoading}
              setFeedback={setFeedback}
            />

            <hr className="my-6 border-t border-gray-300" />

            <form onSubmit={handleSubmit(onOAIVersionSubmit)} className="pb-6 border-b mb-6">
              <div className="flex items-start gap-6 justify-between max-sm:flex-col">
                <div className="sm:max-w-sm">
                  <SectionHeader
                    title="AI Model"
                    description="Select the OpenAI model version used by your chatbot."
                  />
                </div>

                <div className="bg-white p-6 rounded-lg shadow border border-gray-200 w-full sm:max-w-xl">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Model Version
                    </label>
                    {chatbot && (
                      <select
                        id="ai_model"
                        value={chatbot.ai_model || ""}
                        onChange={e =>
                          setChatbot({ ...chatbot, ai_model: e.target.value || "" })
                        }
                        className="border-none focus:outline-inputFocused outline-secondaryBorder disabled:bg-secondaryHover placeholder-inputPlaceholder text-inputText outline outline-1 focus:outline-2 w-full px-3 py-2 rounded-lg shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] text-base font-normal disabled:cursor-not-allowed"
                      >
                        <option value="">Auto</option>
                        <option value="gpt-5-mini">GPT-5 Mini</option>
                        <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                        <option value="gpt-4o-mini">GPT-4o Mini</option>
                      </select>
                    )}
                  </div>

                  <div className="flex gap-4 justify-end items-start">
                    <div>
                      <SecondaryButton
                        onClick={() =>
                          setChatbot({ ...chatbot, ai_model: chatbot.ai_model || "" })
                        }
                        type="button"
                      >
                        Cancel
                      </SecondaryButton>
                    </div>

                    <div>
                      <PrimaryButton>Save Model</PrimaryButton>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </CenterWrapper>
  );
};

export default CompanyInfo;
